import React from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { offerStandaloneService, getStandaloneOfferUrlSegment, type OfferStandalone } from '../services/offerStandaloneService';
import {
  getEstimateList,
  getEstimate,
  getEstimateEasyPrzedmiar,
  updateEstimateStatus,
  type EstimateHeader,
  type EstimateFull,
} from '../services/estimateService';
import { useNotification } from '../components/layouts/Layout';
import AddEstimateOfferDrawer from '../components/easyEstimate/AddEstimateOfferDrawer';
import EasyEstimateOfferKosztorysyTable from '../components/easyEstimate/EasyEstimateOfferKosztorysyTable';
import {
  countEstimatePositionsWithPositiveUnitPrice,
  countPozycjeRowsInEasyPrzedmiar,
  countPozycjeWithCenaJednInEasyPrzedmiar,
  sumNettoFromEasyPrzedmiarRows,
  type PozycjeKosztorysStats,
} from '../utils/sumNetFromEasyPrzedmiarRows';
import {
  clearIntentOpenVerifiedPositionsPage,
  grantVerifiedPositionsPageForEstimate,
  hasIntentOpenVerifiedPositionsPage,
} from '../utils/easyEstimateVerifiedIntent';

/** VAT z karty kosztorysu (`vat` = procent, np. 23). Netto: przedmiar EasyEstimate, inaczej suma `positions`. */
function computeMoneyTotals(full: EstimateFull, netFromPrzedmiar: number | null) {
  const netPositions = (full.positions || []).reduce((s, p) => s + (parseFloat(String(p.value)) || 0), 0);
  const net =
    netFromPrzedmiar !== null
      ? Math.round(netFromPrzedmiar * 100) / 100
      : Math.round(netPositions * 100) / 100;
  const vatPct = (parseFloat(String(full.vat)) || 23) / 100;
  const vat = Math.round(net * vatPct * 100) / 100;
  const brutto = Math.round((net + vat) * 100) / 100;
  return { net, vat, brutto };
}

const EasyEstimateOfferKosztorysyPage: React.FC = () => {
  const { offerId = '' } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [offer, setOffer] = React.useState<OfferStandalone | null>(null);
  const [headers, setHeaders] = React.useState<EstimateHeader[]>([]);
  const [totalsById, setTotalsById] = React.useState<Record<number, { net: number; vat: number; brutto: number }>>({});
  const [pozycjeStatsById, setPozycjeStatsById] = React.useState<Record<number, PozycjeKosztorysStats>>({});
  const [loading, setLoading] = React.useState(true);
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [menuEstimateId, setMenuEstimateId] = React.useState<number | null>(null);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<EstimateHeader | null>(null);
  const [addOpen, setAddOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!offerId) return;
    setLoading(true);
    try {
      const o = await offerStandaloneService.getOfferById(decodeURIComponent(offerId));
      const list = await getEstimateList({ standaloneOfferId: o.id });
      setOffer(o);
      setHeaders(list);
      const totals: Record<number, { net: number; vat: number; brutto: number }> = {};
      const pozycje: Record<number, PozycjeKosztorysStats> = {};
      /* Na ofertę jest z założenia ≤10 kosztorysów — równoległe GET /estimates/:id + /easy-przedmiar na wiersz (~20 żądań) jest w normie. */
      await Promise.all(
        list.map(async (h) => {
          try {
            const [full, przedmiar] = await Promise.all([getEstimate(h.id), getEstimateEasyPrzedmiar(h.id)]);
            const usePrzedmiar =
              przedmiar.success && Array.isArray(przedmiar.rows) && przedmiar.rows.length > 0;
            const netFromPrzedmiar = usePrzedmiar ? sumNettoFromEasyPrzedmiarRows(przedmiar.rows) : null;
            totals[h.id] = computeMoneyTotals(full, netFromPrzedmiar);
            if (usePrzedmiar) {
              const rows = przedmiar.rows;
              pozycje[h.id] = {
                total: countPozycjeRowsInEasyPrzedmiar(rows),
                withCenaJedn: countPozycjeWithCenaJednInEasyPrzedmiar(rows),
              };
            } else {
              const pos = full.positions || [];
              pozycje[h.id] = {
                total: pos.length,
                withCenaJedn: countEstimatePositionsWithPositiveUnitPrice(pos),
              };
            }
          } catch {
            totals[h.id] = { net: 0, vat: 0, brutto: 0 };
            pozycje[h.id] = { total: 0, withCenaJedn: 0 };
          }
        })
      );
      setTotalsById(totals);
      setPozycjeStatsById(pozycje);
    } catch (e: unknown) {
      let msg = 'Nie udało się wczytać danych oferty lub kosztorysów';
      if (axios.isAxiosError(e)) {
        const st = e.response?.status;
        if (st === 404) {
          msg =
            'Nie znaleziono oferty o podanym identyfikatorze. Sprawdź adres (np. rok w OF.396.RRRR) lub przejdź do widoku z listy EasyEstimate.';
        } else if (e.code === 'ERR_NETWORK' || !e.response) {
          msg = 'Brak połączenia z serwerem. Sprawdź połączenie sieciowe i czy backend jest uruchomiony.';
        }
      }
      showNotification(msg, 'error');
      setOffer(null);
      setHeaders([]);
      setTotalsById({});
      setPozycjeStatsById({});
    } finally {
      setLoading(false);
    }
  }, [offerId, showNotification]);

  React.useEffect(() => {
    load();
  }, [load]);

  /** UUID lub błędny rok w OF.seq.RRRR → przekieruj na kanoniczny segment (np. OF.396.2026). */
  React.useEffect(() => {
    if (!offer || !offerId) return;
    const canonical = getStandaloneOfferUrlSegment(offer);
    const decoded = decodeURIComponent(offerId);
    if (canonical && canonical !== decoded) {
      navigate(`/app/easy-estimate/offer/${encodeURIComponent(canonical)}`, { replace: true });
    }
  }, [offer, offerId, navigate]);

  const displayOfferLabel = offer
    ? (offer as { offer_id?: string }).offer_id ||
      (offer.offer_sequence_number && offer.created_at
        ? `OF.${offer.offer_sequence_number}.${new Date(offer.created_at).getFullYear()}`
        : offer.id.substring(0, 8))
    : '';

  React.useEffect(() => {
    const titlePart = displayOfferLabel ? `${displayOfferLabel} · Kosztorysy` : 'Kosztorysy · EasyEstimate';
    try {
      document.title = titlePart;
    } catch {}
    return () => {
      try {
        document.title = 'Unitresco';
      } catch {}
    };
  }, [displayOfferLabel]);

  const openMenu = (e: React.MouseEvent<HTMLElement>, id: number) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuEstimateId(id);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuEstimateId(null);
  };

  const navigateToEstimate = React.useCallback(
    (id: number) => {
      if (hasIntentOpenVerifiedPositionsPage()) {
        clearIntentOpenVerifiedPositionsPage();
        grantVerifiedPositionsPageForEstimate(id);
        navigate(`/app/easy-estimate/estimate/${id}/zweryfikowane`);
      } else {
        navigate(`/app/easy-estimate/estimate/${id}`);
      }
    },
    [navigate]
  );

  const openEditFromMenu = () => {
    if (menuEstimateId != null) {
      navigateToEstimate(menuEstimateId);
    }
    closeMenu();
  };

  const confirmDelete = () => {
    if (menuEstimateId != null) {
      const h = headers.find((x) => x.id === menuEstimateId);
      if (h) {
        setDeleteTarget(h);
        setDeleteOpen(true);
      }
    }
    closeMenu();
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    try {
      await updateEstimateStatus(deleteTarget.id, 'deleted');
      showNotification('Kosztorys usunięty', 'success');
      setDeleteOpen(false);
      setDeleteTarget(null);
      await load();
    } catch {
      showNotification('Nie udało się usunąć kosztorysu', 'error');
    }
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', pb: 4 }}>
      <Typography
        component={RouterLink}
        to="/app/easy-estimate"
        sx={{
          display: 'inline-block',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '14px',
          color: '#3F3FDC',
          textDecoration: 'none',
          mb: 2,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        ← EasyEstimate
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '20px',
              lineHeight: '24px',
              fontWeight: 400,
              color: '#020242',
              m: 0,
              mt: '8.5px',
            }}
          >
            {displayOfferLabel ? (
              <>
                <Typography component="span" sx={{ color: '#020242', fontSize: '20px', fontWeight: 500 }}>
                  {displayOfferLabel}
                </Typography>
                <Typography component="span" sx={{ color: '#5F6368', fontWeight: 400, fontSize: '18px' }}>
                  {' '}
                  — Kosztorysy
                </Typography>
              </>
            ) : (
              <Typography component="span" sx={{ color: '#5F6368', fontSize: '18px' }}>
                Kosztorysy
              </Typography>
            )}
          </Typography>
          {offer && (
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#5F6368', mt: 1 }}>
              {offer.client_name}
              {offer.order_name ? ` · ${offer.order_name}` : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Button
            variant="contained"
            onClick={() => setAddOpen(true)}
            disabled={!offerId || !offer}
            data-action="add"
            sx={{
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' },
            }}
          >
            Dodaj
          </Button>
        </Box>
      </Box>

      <EasyEstimateOfferKosztorysyTable
        headers={headers}
        totalsById={totalsById}
        pozycjeStatsById={pozycjeStatsById}
        loading={loading}
        onRowClick={(id) => navigateToEstimate(id)}
        onMenuButtonClick={(e, id) => openMenu(e, id)}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={openEditFromMenu} sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>
          Edytuj
        </MenuItem>
        <MenuItem
          onClick={confirmDelete}
          sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#c62828' }}
        >
          Usuń
        </MenuItem>
      </Menu>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Usunąć kosztorys?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Kosztorys {deleteTarget?.estimate_number || deleteTarget?.id} zostanie oznaczony jako usunięty.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Anuluj</Button>
          <Button color="error" variant="contained" onClick={runDelete}>
            Usuń
          </Button>
        </DialogActions>
      </Dialog>

      <AddEstimateOfferDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        offer={offer}
        offerId={offer?.id ?? ''}
        displayOfferLabel={displayOfferLabel}
        onSuccess={(id) => {
          showNotification('Kosztorys utworzony', 'success');
          navigateToEstimate(id);
        }}
        onError={(msg) => showNotification(msg, 'error')}
      />
    </Box>
  );
};

export default EasyEstimateOfferKosztorysyPage;
